import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { withTranslation, useTranslation } from 'react-i18next';
import * as tcRenewalAction from 'features/tc-renewal/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import { connect } from 'react-redux';
import { EditDate } from 'features/tc-renewal/index';
import { getMappedCycleEvents } from 'features/tc-renewal/selectors';
import { TestEvent } from 'shared/models';
import { AlertMessage, AlertNotice } from 'shared/components';
import { ASSIGNMENT_STATUS } from 'shared/constants';
import isArray from 'lodash/isArray';
import omit from 'lodash/omit';
import MaterialIcon from 'material-icons-react';
import { Grid } from '@mui/material';

import './TCTestDates.scss';

const noEventSelected = (events) => !events.some((ev) => ev.selected);

const totalCapacity = (ev, types) => {
  const completed = ev.locationEvents
    .flatMap((le) => le.locationEventCapacities)
    .filter((le) => le.capacity).length;
  return completed
    ? ev.locationEvents
        .flatMap((le) => le.locationEventCapacities)
        .filter((le) => types.includes(le.capacityType.type))
        .reduce((acc, le) => acc + Number(le.capacity) || 0, 0)
    : '-';
};

const noCapacity = (events) =>
  events.some(
    (ev) =>
      !ev.locationEvents.length ||
      ev.locationEvents.some(
        (le) =>
          le.selected &&
          le.locationEventCapacities.reduce((acc, lec) => acc + Number(lec.capacity), 0) === 0
      )
  );

export class TCTestDates extends Component {
  constructor(props) {
    super(props);

    this.state = {
      tcCycleEvents: [],
      editModalOpen: false,
      showSuccessMessage: false,
      noCapacityError: false,
      capacityForAll: false,
      currentLocations: [],
      currentEvent: {},
      isDirty: false,
    };

    this.updateSelectedEvents = this.updateSelectedEvents.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.mapLocations = this.mapLocations.bind(this);
    this.optForTestDate = this.optForTestDate.bind(this);
    this.optOutTestDates = this.optOutTestDates.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    this.checkIsDirty = this.checkIsDirty.bind(this);
  }

  componentDidMount() {
    const { tcCycle, actions } = this.props;

    actions.getAvailableRenewalDates(tcCycle.id);
    actions.getCapacityTypes(tcCycle.cycle.id);
    actions.getTcCycleEvents(tcCycle.id);
  }

  componentDidUpdate(prevProps) {
    const { availableRenewalDates, loadingTcCycleEvents, mappedCycleEvents } = this.props;
    const { isDirty } = this.state;

    if (
      availableRenewalDates.length &&
      !loadingTcCycleEvents &&
      !isDirty &&
      (prevProps.availableRenewalDates !== availableRenewalDates ||
        prevProps.loadingTcCycleEvents !== loadingTcCycleEvents)
    ) {
      this.setState({
        tcCycleEvents: mappedCycleEvents,
      });
    }
  }

  updateSelectedEvents(eventData) {
    const { tcCycleEvents } = this.state;
    if (isArray(eventData)) {
      this.setState({
        tcCycleEvents: [...eventData],
        isDirty: true,
      });
    } else {
      const idx = tcCycleEvents.findIndex((ev) => ev.testingEvent.id === eventData.testingEvent.id);
      tcCycleEvents[idx] = { ...eventData };
      this.setState({
        tcCycleEvents,
        isDirty: true,
      });
    }

    this.closeModal();
    this.setState({
      noCapacityError: false,
      showSuccessMessage: true,
    });
  }

  mapLocations(data) {
    const { locations } = this.props.tcCycle;
    const mappedLocations = [];

    locations.forEach((loc) => {
      let newLoc = {
        name: loc.name,
        area: loc.area,
        locationId: loc.id,
        checked: !data?.locationEvents.length,
        errors: {},
      };

      let existentLocation;
      if (data) {
        existentLocation = data.locationEvents.find((locEv) => locEv.locationId === loc.id);
        if (existentLocation) {
          newLoc = {
            ...existentLocation,
            ...newLoc,
            checked: existentLocation.status !== 'CLOSED',
          };
        }
      }

      mappedLocations.push(newLoc);
    });
    return mappedLocations;
  }

  toggleModal(data) {
    const currentLocations = this.mapLocations(data);

    this.setState((prevState) => ({
      currentLocations,
      currentEvent: data || {},
      capacityForAll: !data,
      editModalOpen: !prevState.editModalOpen,
      noCapacityError: false,
    }));
  }

  closeModal() {
    this.setState({
      currentEvent: {},
      currentLocations: [],
      capacityForAll: false,
      editModalOpen: false,
    });
  }

  optForTestDate(event) {
    const { tcCycleEvents } = this.state;
    const eventsList = [...tcCycleEvents];

    const idx = eventsList.findIndex((ev) => ev.testingEvent.id === event.testingEvent.id);
    eventsList[idx] = {
      ...eventsList[idx],
      testCenterEventStatus: tcCycleEvents[idx].selected
        ? ASSIGNMENT_STATUS.closed
        : ASSIGNMENT_STATUS.open,
      selected: !tcCycleEvents[idx].selected,
    };

    this.setState({
      tcCycleEvents: eventsList,
      noCapacityError: false,
      isDirty: true,
    });
    this.closeModal();
  }

  optOutTestDates() {
    const { tcCycleEvents } = this.state;

    tcCycleEvents.forEach((tcEv) => {
      if (tcEv.selected) {
        tcEv.testCenterEventStatus = ASSIGNMENT_STATUS.closed;
        tcEv.selected = false;
      }
    });

    this.setState({
      tcCycleEvents,
      noCapacityError: false,
      isDirty: true,
    });
    this.closeModal();
  }

  removeMessage() {
    this.setState({
      showSuccessMessage: false,
    });
  }

  async handleContinue() {
    const { tcCycleEvents } = this.state;

    const selectedEvents = tcCycleEvents
      .filter((ev) => ev.selected)
      .map((ev) => new TestEvent(omit(ev, 'selected')));
    const existingEvents = tcCycleEvents
      .filter((ev) => ev.id && !ev.selected)
      .map((ev) =>
        new TestEvent({
          ...omit(ev, 'selected'),
          locationEvents: ev.locationEvents.map((le) => ({
            ...le,
            status: 'CLOSED',
            locationEventCapacities: [],
          })),
        })
      );

    if (noCapacity(selectedEvents)) {
      this.setState({
        noCapacityError: true,
      });
      return false;
    }

    const cycleUpdated = await this.props.actions.setTcCycleEvents(this.props.tcCycle.id, [
      ...selectedEvents,
      ...existingEvents,
    ]);

    if (cycleUpdated?.message) {
      return { error: true };
    }

    return !!cycleUpdated;
  }

  checkIsDirty() {
    return this.state.isDirty;
  }

  render() {
    const {
      tcCycleEvents,
      editModalOpen,
      currentLocations,
      currentEvent,
      capacityForAll,
      noCapacityError,
      showSuccessMessage,
    } = this.state;
    const { capacityTypes } = this.props;

    return (
      !!tcCycleEvents.length && (
        <section className="renewal-dates-section">
          <h4>{this.props.t('testCenter.reviewTestDates.title')}</h4>
          <p
            className="mb-3 message-info"
            dangerouslySetInnerHTML={{
              __html: this.props.t('testCenter.reviewTestDates.subtitle'),
            }}
          />
          {noCapacityError && (
            <AlertNotice
              message={this.props.t('testCenter.reviewTestDates.optOutError')}
              type="error"
            />
          )}
          <button
            className="btn btn-primary btn-small btn-same-capacity"
            disabled={noEventSelected(tcCycleEvents)}
            onClick={() => this.toggleModal()}>
            {this.props.t('testCenter.reviewTestDates.sameCapacityLabel')}
          </button>
          {tcCycleEvents.map((testDate, idx) => (
            <AvailableDates
              key={idx}
              event={testDate}
              toggleModal={this.toggleModal}
              optForTestDate={this.optForTestDate}
            />
          ))}
          <EditDate
            capacityTypes={capacityTypes}
            cycleEvents={tcCycleEvents}
            locationsList={currentLocations}
            event={currentEvent}
            editModalOpen={editModalOpen}
            closeModal={this.closeModal}
            updateSelectedEvents={this.updateSelectedEvents}
            optForTestDate={this.optForTestDate}
            optOutTestDates={this.optOutTestDates}
            capacityForAll={capacityForAll}
          />
          <AlertMessage
            show={showSuccessMessage}
            removeMessage={this.removeMessage}
            message="testCenter.reviewTestDates.successUpdateCapacity"
          />
        </section>
      )
    );
  }
}

function mapStateToProps(state) {
  return {
    tcCycle: state.tcRenewal.tcCycle,
    availableRenewalDates: state.tcRenewal.availableRenewalDates,
    loadingTcCycleEvents: state.tcRenewal.loadingTcCycleEvents,
    tcCycleEvents: state.tcRenewal.tcCycleEvents,
    mappedCycleEvents: getMappedCycleEvents(state),
    locations: state.tcRenewal.tcCycle.locations,
    capacityTypes: state.statics.capacityTypes,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...staticActions, ...tcRenewalAction }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(withTranslation(null, { withRef: true })(TCTestDates));

TCTestDates.propTypes = {
  tcCycle: PropTypes.shape({
    id: PropTypes.number,
    locations: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  availableRenewalDates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  capacityTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tcCycleEvents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mappedCycleEvents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loadingTcCycleEvents: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    getAvailableRenewalDates: PropTypes.func,
    getCapacityTypes: PropTypes.func,
    getTcCycleEvents: PropTypes.func,
    setTcCycleEvents: PropTypes.func,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

const AvailableDates = (props) => {
  const { t } = useTranslation();
  return (
    <div className={`date-row ${!props.event.selected ? 'opted-out' : ''}`}>
      <div className="left-section">
        <p className="fw-bold mb-1">{props.event.testingEvent.name}</p>
        {props.event.selected ? (
          <Grid className="capacity-info font-size-12" container spacing={1}>
            <Grid item xs={6}>
              {t('testCenter.reviewTestDates.paperStandard')}{' '}
              {totalCapacity(props.event, ['REGULAR', 'PLUS WRITING'])}
            </Grid>
            <Grid item xs={6}>
              {t('testCenter.reviewTestDates.onlineStandard')}{' '}
              {totalCapacity(props.event, ['ONLINE REGULAR', 'BYOD REGULAR'])}
            </Grid>
            <Grid item xs={6}>
              {t('testCenter.reviewTestDates.paperExtended')}{' '}
              {totalCapacity(props.event, ['SPECIAL-AUTO'])}
            </Grid>
            <Grid item xs={6}>
              {t('testCenter.reviewTestDates.onlineExtended')}{' '}
              {totalCapacity(props.event, ['ONLINE SPECIAL-AUTO', 'BYOD SPECIAL-AUTO'])}
            </Grid>
            <Grid item xs={6}>
              {t('testCenter.reviewTestDates.paperOneToOne')}{' '}
              {totalCapacity(props.event, ['SPECIAL-MANUAL'])}
            </Grid>
            <Grid item xs={6}>
              {t('testCenter.reviewTestDates.onlineOneToOne')}{' '}
              {totalCapacity(props.event, ['ONLINE SPEC-MANUAL', 'BYOD SPEC-MANUAL'])}
            </Grid>
          </Grid>
        ) : (
          <p className="mb-0 font-size-12">{t('testCenter.reviewTestDates.optedOutMessage')}</p>
        )}
      </div>
      <div className="d-none d-md-block">
        <button
          className="btn-link text-pacific-blue btn-edit text-strong"
          disabled={!props.event.selected}
          onClick={() => props.toggleModal(props.event)}>
          <span className="fw-bold">
            <i className="fa fa-pencil" />
            {t('testCenter.reviewTestDates.editCapacityLabel')}
          </span>
        </button>
        <button
          className="btn-link text-pacific-blue text-strong"
          onClick={() => props.optForTestDate(props.event)}>
          <span className="fw-bold">
            <i className={`fa ${props.event.selected ? 'fa-sign-out' : 'fa-sign-in'}`} />
            {props.event.selected
              ? t('testCenter.reviewTestDates.optOutLabel')
              : t('testCenter.reviewTestDates.optInLabel')}
          </span>
        </button>
      </div>

      <div className="dropdown d-md-none">
        <div className="btn-options" data-bs-toggle="dropdown">
          {t('common.labels.options')}
          <MaterialIcon icon="more_vert" size="small" />
        </div>
        <div className="dropdown-menu dropdown-menu-right">
          <button
            className="btn-link text-pacific-blue btn-edit text-strong"
            disabled={!props.event.selected}
            onClick={() => props.toggleModal(props.event)}>
            <span className="fw-bold">
              <i className="fa fa-pencil" />
              {t('testCenter.reviewTestDates.editCapacityLabel')}
            </span>
          </button>
          <button
            className="btn-link text-pacific-blue text-strong"
            onClick={() => props.optForTestDate(props.event)}>
            <span className="fw-bold">
              <i className={`fa ${props.event.selected ? 'fa-sign-out' : 'fa-sign-in'}`} />
              {props.event.selected
                ? t('testCenter.reviewTestDates.optOutLabel')
                : t('testCenter.reviewTestDates.optInLabel')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

AvailableDates.propTypes = {
  event: PropTypes.shape({
    testingEvent: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    selected: PropTypes.bool,
    locationEvents: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  toggleModal: PropTypes.func.isRequired,
  optForTestDate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};
